import { useCallback, useEffect, useRef, useState } from "react";
import config from "../config/environment";

interface FBLoginResponse {
  authResponse?: {
    code: string;
    accessToken?: string;
    userID?: string;
  };
  status: "connected" | "not_authorized" | "unknown";
}

declare global {
  interface Window {
    fbAsyncInit: () => void;
    FB: {
      init: (params: {
        appId: string;
        cookie: boolean;
        xfbml: boolean;
        version: string;
      }) => void;
      login: (
        callback: (response: FBLoginResponse) => void,
        options: Record<string, unknown>
      ) => void;
    };
  }
}

interface EmbeddedSignupSessionInfo {
  phone_number_id: string;
  waba_id: string;
}

export interface EmbeddedSignupResult {
  code: string;
  phone_number_id: string;
  waba_id: string;
}

export interface MetaLoginResult {
  code: string;
}

const SDK_SCRIPT_ID = "facebook-jssdk";
const SDK_VERSION = "v21.0";

let sdkLoadPromise: Promise<void> | null = null;
let initializedAppId: string | null = null;

const loadMetaSdkScript = (): Promise<void> => {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Meta SDK requires a browser environment"));
  }

  if (window.FB && initializedAppId) {
    return Promise.resolve();
  }

  if (sdkLoadPromise) return sdkLoadPromise;

  sdkLoadPromise = new Promise((resolve, reject) => {
    const finishInit = (appId: string) => {
      window.fbAsyncInit = () => {
        window.FB.init({
          appId,
          cookie: true,
          xfbml: true,
          version: SDK_VERSION,
        });
        initializedAppId = appId;
        resolve();
      };

      if (window.FB) {
        window.FB.init({
          appId,
          cookie: true,
          xfbml: true,
          version: SDK_VERSION,
        });
        initializedAppId = appId;
        resolve();
        return;
      }

      if (!document.getElementById(SDK_SCRIPT_ID)) {
        const script = document.createElement("script");
        script.id = SDK_SCRIPT_ID;
        script.src = "https://connect.facebook.net/en_US/sdk.js";
        script.async = true;
        script.defer = true;
        script.crossOrigin = "anonymous";
        script.onerror = () => reject(new Error("Failed to load Meta SDK"));
        document.body.appendChild(script);
      }
    };

    finishInit(config.metaWaAppId || config.metaAppId);
  });

  return sdkLoadPromise;
};

const ensureMetaSdk = async (appId: string) => {
  await loadMetaSdkScript();
  if (!window.FB) {
    throw new Error("Meta SDK not loaded");
  }
  if (initializedAppId !== appId) {
    window.FB.init({
      appId,
      cookie: true,
      xfbml: true,
      version: SDK_VERSION,
    });
    initializedAppId = appId;
  }
};

const runMetaLogin = (options: Record<string, unknown>): Promise<string> =>
  new Promise((resolve, reject) => {
    if (!window.FB) {
      reject(new Error("Meta SDK not loaded"));
      return;
    }

    window.FB.login((response: FBLoginResponse) => {
      if (response.authResponse?.code) {
        resolve(response.authResponse.code);
        return;
      }
      reject(new Error("User cancelled or login failed"));
    }, options);
  });

const runMetaRedirectLogin = ({
  appId,
  channel,
  scope,
  configId,
}: {
  appId: string;
  channel: "messenger" | "instagram";
  scope: string;
  configId?: string;
}): Promise<string> =>
  new Promise((_resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("Meta OAuth requires a browser environment"));
      return;
    }

    const state =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const oauthState = `fluxi_meta_oauth:${channel}:${state}`;
    const redirectUri = `${window.location.origin}/integrations`;
    const params = new URLSearchParams({
      client_id: appId,
      redirect_uri: redirectUri,
      response_type: "code",
      scope,
      state: oauthState,
    });
    if (configId) params.set("config_id", configId);

    window.location.assign(`https://www.facebook.com/${SDK_VERSION}/dialog/oauth?${params.toString()}`);
  });

const runInstagramRedirectLogin = ({
  appId,
  scope,
}: {
  appId: string;
  scope: string;
}): Promise<string> =>
  new Promise((_resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("Instagram OAuth requires a browser environment"));
      return;
    }

    const state =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const oauthState = `fluxi_meta_oauth:instagram:${state}`;
    const redirectUri = `${window.location.origin}/integrations`;
    const params = new URLSearchParams({
      client_id: appId,
      redirect_uri: redirectUri,
      response_type: "code",
      scope,
      state: oauthState,
    });

    window.location.assign(`https://www.instagram.com/oauth/authorize?${params.toString()}`);
  });

export const launchWhatsAppEmbeddedSignup = async (): Promise<EmbeddedSignupResult> => {
  const appId = config.metaWaAppId || config.metaAppId;
  await ensureMetaSdk(appId);

  const sessionInfoRef: { current: EmbeddedSignupSessionInfo | null } = { current: null };

  const handleMessage = (event: MessageEvent) => {
    if (event.origin !== "https://www.facebook.com" && event.origin !== "https://web.facebook.com") {
      return;
    }
    try {
      const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      if (data.type === "WA_EMBEDDED_SIGNUP") {
        sessionInfoRef.current = {
          phone_number_id: data.data?.phone_number_id || "",
          waba_id: data.data?.waba_id || "",
        };
      }
    } catch {
      // Ignore non-JSON messages
    }
  };

  window.addEventListener("message", handleMessage);

  try {
    const code = await runMetaLogin({
      config_id: config.metaWaConfigId || config.metaConfigId,
      response_type: "code",
      override_default_response_type: true,
      extras: {
        feature: "whatsapp_embedded_signup",
        sessionInfoVersion: 3,
      },
    });

    const waitForSessionInfo = (retries: number): Promise<EmbeddedSignupSessionInfo> =>
      new Promise((resolve, reject) => {
        if (sessionInfoRef.current) {
          resolve(sessionInfoRef.current);
          return;
        }
        if (retries <= 0) {
          reject(new Error("Session info (phone_number_id, waba_id) not received from Meta"));
          return;
        }
        window.setTimeout(() => {
          waitForSessionInfo(retries - 1).then(resolve).catch(reject);
        }, 200);
      });

    const sessionInfo = await waitForSessionInfo(10);
    return {
      code,
      phone_number_id: sessionInfo.phone_number_id,
      waba_id: sessionInfo.waba_id,
    };
  } finally {
    window.removeEventListener("message", handleMessage);
  }
};

export const launchMessengerLogin = async (): Promise<MetaLoginResult> => {
  const appId = config.metaMessengerAppId || config.metaAppId;
  const code = await runMetaRedirectLogin({
    appId,
    channel: "messenger",
    configId: config.metaMessengerConfigId || undefined,
    scope: "pages_messaging,pages_show_list,pages_manage_metadata,business_management",
  });
  return { code };
};

export const launchInstagramLogin = async (): Promise<MetaLoginResult> => {
  const appId = config.metaInstagramAppId || config.metaAppId;
  const code = await runInstagramRedirectLogin({
    appId,
    scope: "instagram_business_basic,instagram_business_manage_messages",
  });
  return { code };
};

export const useMetaSDK = (appId?: string) => {
  const resolvedAppId = appId || config.metaWaAppId || config.metaAppId;
  const [sdkReady, setSdkReady] = useState(false);
  const sessionInfoRef = useRef<EmbeddedSignupSessionInfo | null>(null);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== "https://www.facebook.com" && event.origin !== "https://web.facebook.com") {
        return;
      }
      try {
        const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        if (data.type === "WA_EMBEDDED_SIGNUP") {
          sessionInfoRef.current = {
            phone_number_id: data.data?.phone_number_id || "",
            waba_id: data.data?.waba_id || "",
          };
        }
      } catch {
        // Ignore non-JSON messages
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  useEffect(() => {
    let cancelled = false;
    ensureMetaSdk(resolvedAppId)
      .then(() => {
        if (!cancelled) setSdkReady(true);
      })
      .catch(() => {
        if (!cancelled) setSdkReady(false);
      });
    return () => {
      cancelled = true;
    };
  }, [resolvedAppId]);

  const launchEmbeddedSignup = useCallback(async (): Promise<EmbeddedSignupResult> => {
    sessionInfoRef.current = null;
    const result = await launchWhatsAppEmbeddedSignup();
    sessionInfoRef.current = {
      phone_number_id: result.phone_number_id,
      waba_id: result.waba_id,
    };
    return result;
  }, []);

  return {
    sdkReady,
    launchWhatsAppEmbeddedSignup: launchEmbeddedSignup,
    launchMessengerLogin,
    launchInstagramLogin,
  };
};
